import { modelOptions, PropType, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, milliseconds, validate } from "../../../zod-decorator/index.js";
import { ManualOverrideInfo } from "../../commonSubSchemas/manualOverrideInfo.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type {
    SproutNetworkInfoFieldsWithDefaults,
    SproutNetworkInfoSchemaTypes,
} from "../../types/sproutNetworkInfo.d.js";
import { type DtoValidatorTypes } from "../../validatableClass.js";
import { SignalMetrics } from "./signalMetrics.js";
import { SproutLastDownlinkInfo } from "./sproutLastDownlinkInfo.js";
import { SproutRecentPortHistory } from "./sproutRecentPortHistory.js";

const SCHEMA_KEY = "SproutNetworkInfo" as const;

const defaultLastDownlink = {
    schemaKey: SproutLastDownlinkInfo.schemaKey,
    bytes: "NONE",
    fPort: -1,
    idemp: -1,
    date: new Date(0),
    flaggedAsReceived: false,
    flaggedAsFailed: false,
    interactionID: undefined,
} as const satisfies SproutLastDownlinkInfo;

// TODO: mark as responseOnly schema class
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<SproutNetworkInfo>>(...addSchemaKeyHook(SCHEMA_KEY))
export class SproutNetworkInfo extends SchemaBase<SproutNetworkInfo, SproutNetworkInfoFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({
        childClass: SproutLastDownlinkInfo,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => SproutLastDownlinkInfo, required: true, default: defaultLastDownlink })
    public lastDownlink: SproutLastDownlinkInfo = defaultLastDownlink;

    /**
     * Specifically for sensor uplinks to ensure we don't add duplicate data
     * @type {Record<number, Date>}
     */
    @validate({ zodValidator: z.record(z.string(), z.coerce.date()), responseOnly: true })
    @prop({ type: () => Date, required: true }, PropType.MAP)
    public timeOfLastSensorReadingBySensorPort!: Map<string, Date>;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastNetworkTestStartDate?: Date;

    @validate({
        childClass: SproutRecentPortHistory,
        childClassTransform: (object) => z.record(z.string(), object),
        responseOnly: true,
    })
    @prop({ type: () => SproutRecentPortHistory, required: true }, PropType.MAP)
    public lastContactByPort!: Map<string, SproutRecentPortHistory>;

    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: -1 })
    public lastErrorCode: number = -1;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: true, default: () => new Date(0) })
    public timeOfLastVerifiedCorrectClock: Date = new Date(0);

    @validate({
        childClass: SignalMetrics,
        childClassTransform: (object) => z.array(object).optional(),
        responseOnly: true,
    })
    @prop({ type: () => SignalMetrics, required: true, default: [] }, PropType.ARRAY)
    public lastSignalMetrics: SignalMetrics[] = [];

    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    public packetLoss: number = 0;

    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    public uplinkPacketLoss: number = 0;

    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    public downlinkPacketLoss: number = 0;

    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    public crashCount: number = 0;

    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: true, default: 0 })
    public resetCount: number = 0;

    @validate({ zodValidator: z.number().optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public batteryVoltage?: number;

    @validate({
        childClass: ManualOverrideInfo,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => ManualOverrideInfo, required: false })
    public manualOverrideInfo?: ManualOverrideInfo;

    /**
     * Device uplink period in milliseconds when the device is not in
     * active hours.
     *
     * Stored here as a multiple of 15 seconds, but encoded during
     * downlinks as ⌊currentStandardUplinkPeriod / 15000⌋.
     *
     * Gen3 devices support a value range of [1, 255] which represents
     * the number of minutes between uplinks.
     *
     * Changing this value has no effect, it is overwritten every time new information on the current uplink
     * period is received from the device.
     */
    @validate({ zodValidator: milliseconds.optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public currentStandardUplinkPeriod?: number;

    /**
     * Device uplink period in milliseconds when the device is in
     * active hours, a daily window of time established by the:
     * - active hours start time (time past 00:00 UTC) and
     * - active hours length (in 5 minute increments past start).
     *
     * Stored here as a multiple of 15 seconds, but encoded during
     * downlinks as ⌊currentStandardUplinkPeriod / 15000⌋.
     *
     * Gen3 devices support a value range of [1, 255] which represents
     * the number of minutes between uplinks.
     *
     * Changing this value has no effect, it is overwritten every time new information on the current uplink
     * period is received from the device.
     */
    @validate({ zodValidator: milliseconds.optional(), responseOnly: true })
    @prop({ type: () => Number, required: false })
    public currentActiveUplinkPeriod?: number;

    static getters = {} as const satisfies Record<string, (self: SproutNetworkInfo) => any>;

    static utilities = {
        getDefaultSproutNetworkInfo: (): SproutNetworkInfo =>
            ({
                batteryVoltage: 0,
                crashCount: 0,
                downlinkPacketLoss: 0,
                lastContactByPort: new Map(),
                lastDownlink: {
                    bytes: "NONE",
                    date: new Date(0),
                    fPort: 0,
                    flaggedAsFailed: false,
                    flaggedAsReceived: false,
                    idemp: 0,
                },
                timeOfLastSensorReadingBySensorPort: new Map(),
                timeOfLastVerifiedCorrectClock: new Date(0),
                lastErrorCode: 0,
                lastSignalMetrics: [],
                packetLoss: 0,
                resetCount: 0,
                uplinkPacketLoss: 0,
            }) as SproutNetworkInfo,
    } as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<SproutNetworkInfoSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<SproutNetworkInfo, SproutNetworkInfoFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

SproutNetworkInfo.dtoValidators = getZodSchemaFromClass(
    SproutNetworkInfo,
) as unknown as DtoValidatorTypes<SproutNetworkInfoSchemaTypes>;
